package br.com.heyagenda.domain.model

enum class BillingCycle(val id: String, val title: String) {
    MONTHLY("monthly", "Mensal"),
    ANNUAL("annual", "Anual")
}

enum class SubscriptionTier(
    val id: String,
    val title: String,
    val regularMonthlyPrice: Double,
    val regularAnnualMonthlyPrice: Double,
    val regularAnnualTotal: Double,
    val founderMonthlyPrice: Double,
    val founderAnnualMonthlyPrice: Double,
    val founderAnnualTotal: Double,
    val maxAppointmentsPerMonth: Int,
    val maxClients: Int,
    val maxServices: Int,
    val hasAds: Boolean,
    val hasWatermark: Boolean,
    val hasFinancialClosing: Boolean,
    val hasAutoWhatsAppReminders: Boolean
) {
    FREE(
        id = "free",
        title = "Grátis",
        regularMonthlyPrice = 0.0,
        regularAnnualMonthlyPrice = 0.0,
        regularAnnualTotal = 0.0,
        founderMonthlyPrice = 0.0,
        founderAnnualMonthlyPrice = 0.0,
        founderAnnualTotal = 0.0,
        maxAppointmentsPerMonth = 60,
        maxClients = 30,
        maxServices = 6,
        hasAds = true,
        hasWatermark = true,
        hasFinancialClosing = false,
        hasAutoWhatsAppReminders = false
    ),
    PRO(
        id = "pro",
        title = "Pro",
        regularMonthlyPrice = 19.90,
        regularAnnualMonthlyPrice = 14.90,
        regularAnnualTotal = 178.80,
        founderMonthlyPrice = 14.90,
        founderAnnualMonthlyPrice = 9.90,
        founderAnnualTotal = 118.80,
        maxAppointmentsPerMonth = Int.MAX_VALUE,
        maxClients = Int.MAX_VALUE,
        maxServices = Int.MAX_VALUE,
        hasAds = false,
        hasWatermark = false,
        hasFinancialClosing = true,
        hasAutoWhatsAppReminders = true
    );

    fun effectiveMonthlyPrice(isAnnual: Boolean, isFounder: Boolean): Double {
        return when {
            this == FREE -> 0.0
            isFounder && isAnnual -> founderAnnualMonthlyPrice
            isFounder && !isAnnual -> founderMonthlyPrice
            !isFounder && isAnnual -> regularAnnualMonthlyPrice
            else -> regularMonthlyPrice
        }
    }

    fun effectiveAnnualTotal(isFounder: Boolean): Double {
        return if (isFounder) founderAnnualTotal else regularAnnualTotal
    }

    companion object {
        fun fromId(id: String?): SubscriptionTier {
            return when (id?.trim()?.lowercase()) {
                "pro" -> PRO
                else -> FREE
            }
        }
    }
}

data class SubscriptionInfo(
    val tier: SubscriptionTier = SubscriptionTier.FREE,
    val billingCycle: BillingCycle? = null,
    val isActive: Boolean = true,
    val isFounder: Boolean = true,
    val expiresAt: Long? = null,
    val monthlyAppointmentsUsed: Int = 0
) {
    val isPro: Boolean
        get() = tier == SubscriptionTier.PRO && isActive

    val hasReachedAppointmentLimit: Boolean
        get() = !isPro && monthlyAppointmentsUsed >= tier.maxAppointmentsPerMonth

    val remainingAppointments: Int
        get() = if (isPro) Int.MAX_VALUE else (tier.maxAppointmentsPerMonth - monthlyAppointmentsUsed).coerceAtLeast(0)
}
